<template>
  <table class="w-full">
    <caption>
      {{
        tableCaption
      }}
      ({{
        $t("Tooltips.ColumnHeadersWithButtonsAreSortable")
      }})
    </caption>
    <thead class="bg-gray-100">
      <tr class="border-b border-gray-300" role="row">
        <th v-if="isPeopleSelectable" role="columnheader" scope="col"></th>
        <th
          class="h-14 pl-2 text-left w-auto"
          colspan="2"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn('name')"
        >
          <button
            id="name"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('name')"
            @click="setSortProperties"
          >
            {{ $t("name") }}
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>

        <th
          v-if="$slots.column2Header"
          class="p-2 text-left hidden md:table-cell"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn(column2SortKey)"
        >
          <span v-if="!column2SortKey">
            <slot name="column2Header"></slot>
          </span>
          <button
            v-else
            :id="column2SortKey"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('column2SortKey')"
            @click="setSortProperties"
          >
            <slot name="column2Header"></slot>
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>

        <th
          v-if="$slots.column3Header"
          class="p-2 hidden md:table-cell"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn(column3SortKey)"
        >
          <span v-if="!column3SortKey">
            <slot name="column3Header"></slot>
          </span>
          <button
            v-else
            :id="column3SortKey"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('column3SortKey')"
            @click="setSortProperties"
          >
            <slot name="column3Header"></slot>
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>

        <th
          v-if="$slots.column4Header"
          class="p-2 hidden md:table-cell text-left"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn(column4SortKey)"
        >
          <span v-if="!column4SortKey">
            <slot name="column4Header"></slot>
          </span>
          <button
            v-else
            :id="column4SortKey"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('column4SortKey')"
            @click="setSortProperties"
          >
            <slot name="column4Header"></slot>
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>

        <th
          v-if="$slots.column5Header"
          class="p-2 hidden md:table-cell text-left"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn(column5SortKey)"
        >
          <span v-if="!column5SortKey">
            <slot name="column5Header"></slot>
          </span>
          <button
            v-else
            :id="column5SortKey"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('column5SortKey')"
            @click="setSortProperties"
          >
            <slot name="column5Header"></slot>
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>

        <th
          v-if="$slots.column6Header"
          class="p-2 hidden md:table-cell text-left w-auto"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn(column6SortKey)"
        >
          <span v-if="!column6SortKey">
            <slot name="column6Header"></slot>
          </span>
          <button
            v-else
            :id="column6SortKey"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('column6SortKey')"
            @click="setSortProperties"
          >
            <slot name="column6Header"></slot>
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>
      </tr>
    </thead>
    <tbody v-for="(group, groupIndex) in sortedGroups" :key="groupIndex">
      <tr v-if="showGroups" class="bg-gray-150" role="row">
        <th v-if="isPeopleSelectable" colspan="2"></th>
        <th colspan="2" class="p-2 text-left font-normal uppercase text-sm">{{ group.role }}</th>
      </tr>
      <tr
        v-for="(person, personIndex) in group.people"
        :key="person.userId"
        role="row"
        class="border-b last:border-0 border-gray-200 hover:bg-gray-150"
        :class="{
          'cursor-default': (!isPeopleSelectable && !isPeopleClickable) || isPersonDisabled(person),
          'opacity-60': isPersonDisabled(person),
          'text-danger font-extrabold': isPersonEmphasized(person),
          'cursor-pointer': isPeopleSelectable || (isPeopleClickable && !isPersonDisabled(person))
        }"
        style="user-select: none"
        :data-selected="person.selected && isPeopleSelectable"
        :title="isPersonEmphasized(person) ? emphasizePersonLabel : person.name.name"
        :disabled="isPersonDisabled(person)"
        @click.exact="select(person, personIndex)"
        @click.ctrl.stop.prevent="select(person, personIndex, group, 'ctrl')"
        @click.shift.stop.prevent="select(person, personIndex, group, 'shift')"
      >
        <td v-if="isPeopleSelectable && !isPersonDisabled(person)" class="min-width">
          <div class="flex items-center h-full top-0 pr-2" js-table-checkbox-wrapper>
            <span
              class="flex items-center justify-center w-6 h-6 rounded-full bg-white border border-solid border-gray-200"
              js-table-checkbox
            >
              <span class="w-3 h-3 bg-primary-core rounded-full opacity-0"></span>
            </span>
          </div>
        </td>
        <td class="p-2">
          <PersonAvatar :person="person" :tooltip="false" />
        </td>
        <td class="p-2 pl-2 w-full">
          <router-link v-if="isPeopleClickable" :to="{ name: onClickRouteName, params: calcRouteParams(person) }">{{
            person.name.name
          }}</router-link>
          <span v-else> {{ person.name.name }} </span>

          <div class="md:hidden">
            <div class="flex">
              <div class="text-xs text-gray-500"><slot name="column2Content" :person="person"></slot></div>
              <div class="text-xs text-gray-500 ml-4"><slot name="column3Content" :person="person"></slot></div>
            </div>
            <div class="text-xs text-gray-500"><slot name="column4Content" :person="person"></slot></div>
          </div>
        </td>

        <td v-if="$scopedSlots.column2Content" class="p-2 pr-1 hidden md:table-cell">
          <slot name="column2Content" :person="person"></slot>
        </td>

        <td v-if="$scopedSlots.column3Content" class="p-2 pr-1 text-center hidden md:table-cell">
          <slot name="column3Content" :person="person"></slot>
        </td>

        <td v-if="$scopedSlots.column4Content" class="p-2 pr-1 hidden md:table-cell">
          <slot name="column4Content" :person="person"></slot>
        </td>

        <td v-if="$scopedSlots.column5Content" class="p-2 pr-1 hidden md:table-cell">
          <slot name="column5Content" :person="person"></slot>
        </td>

        <td v-if="$scopedSlots.column6Content" class="p-2 pr-1 hidden md:table-cell w-auto">
          <slot name="column6Content" :person="person"></slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import PersonAvatar from "@/components/People/PersonAvatar";

export default {
  name: "PeopleTable",

  data() {
    return {
      orderByProperty: this.defaultSortKey,
      orderByDirection: this.defaultSortDirection,
      lastSelectedPersonIndex: 0
    };
  },
  props: {
    tableCaption: { type: String, required: true },
    students: { type: Array, default: () => [] },
    teachers: { type: Array, default: () => [] },
    onClickRouteName: { type: String, default: "home" },
    onClickRouteParams: { type: Object, default: () => ({}) },

    disabledPeopleIds: { type: Array, required: false, default: () => [] },
    isPeopleSelectable: { type: Boolean, default: () => false },
    isPeopleClickable: { type: Boolean, default: () => false },
    showGroups: { type: Boolean, default: () => true },
    selectedPeople: { type: Array, default: () => [] },
    emphasizePersonKey: { type: String, default: null },
    emphasizePersonLabel: { type: String, default: null },
    column2SortKey: { type: String, default: () => undefined },
    column3SortKey: { type: String, default: () => undefined },
    column4SortKey: { type: String, default: () => undefined },
    column5SortKey: { type: String, default: () => undefined },
    column6SortKey: { type: String, default: () => undefined },
    defaultSortKey: { type: String, default: "name" },
    defaultSortDirection: { type: String, default: "asc" }
  },

  computed: {
    sortedGroups() {
      const sortedGroups = [];
      if (this.teachers.length > 0) {
        sortedGroups.push({ role: this.$t("People.Roles.Teachers"), people: this.sortPeople(this.teachers) });
      }

      if (this.students.length > 0) {
        sortedGroups.push({ role: this.$t("People.Roles.Students"), people: this.sortPeople(this.students) });
      }

      if (this.orderByDirection === "desc") {
        sortedGroups.reverse();
      }
      return sortedGroups;
    },

    people() {
      return [].concat(this.teachers).concat(this.students);
    }
  },

  methods: {
    select(person, personIndex, group, modifier) {
      if (!this.isPeopleSelectable) {
        this.onClick(person);
      }

      if (this.isPersonDisabled(person)) {
        return;
      }

      let selectedPeopleCp = this.selectedPeople;

      if (person.selected) {
        // Person is already selected
        if (selectedPeopleCp.length > 1) {
          // There are more than this person selected
          if (modifier === "ctrl") {
            // We did press control - remove the current one
            const indexOfSelectedPerson = selectedPeopleCp.findIndex(p => p.userId === person.userId);
            selectedPeopleCp.splice(indexOfSelectedPerson, 1);
          } else {
            // We did not press control - remove all but this one
            selectedPeopleCp = [person];
          }
        } else {
          // There are no selected documents - deselect this one too
          selectedPeopleCp = [];
        }
      } else {
        // If we did not press shift or ctrl them empty the selections
        if (modifier !== "shift" && modifier !== "ctrl") {
          selectedPeopleCp = [];
        }

        // If shift is pressed during click - and we aldready has a selected item in the kist then choose all between the last selected and this one
        if (modifier === "shift" && selectedPeopleCp.length > 0) {
          if (personIndex > this.lastSelectedPersonIndex) {
            selectedPeopleCp.push(...group.people.slice(this.lastSelectedPersonIndex + 1, personIndex));
          } else {
            selectedPeopleCp.push(...group.people.slice(personIndex + 1, this.lastSelectedPersonIndex));
          }
        }
        // And finally select the clicked element
        selectedPeopleCp.push(person);
      }

      this.lastSelectedPersonIndex = personIndex;
      this.$emit("selectedPeopleChanged", selectedPeopleCp);
    },

    setSortProperties(evnt) {
      if (evnt.target.id) {
        if (this.orderByProperty == evnt.target.id) {
          this.orderByDirection = this.orderByDirection === "asc" ? "desc" : "asc";
          this.$emit("orderByDirectionChanged", this.orderByDirection);
        } else {
          this.orderByProperty = evnt.target.id;
          this.orderByDirection = "asc";
          this.$emit("orderByPropertyChanged", this.orderByProperty);
          this.$emit("orderByDirectionChanged", this.orderByDirection);
        }
      } else if (evnt.target.parentElement.id) {
        if (this.orderByProperty == evnt.target.parentElement.id) {
          this.orderByDirection = this.orderByDirection === "asc" ? "desc" : "asc";
          this.$emit("orderByDirectionChanged", this.orderByDirection);
        } else {
          this.orderByProperty = evnt.target.parentElement.id;
          this.orderByDirection = "asc";
          this.$emit("orderByDirectionChanged", this.orderByDirection);
          this.$emit("orderByPropertyChanged", this.orderByProperty);
        }
      } else {
        // eslint-disable-next-line no-console
        console.error("No id on sort elm");
      }
    },

    clearSelection() {
      this.$emit("selectedPeopleChanged", []);
    },

    sortPeople(people) {
      let sortedList = people;

      if (this.orderByProperty === "name") {
        sortedList.sort((a, b) => (a["name"]["name"] < b["name"]["name"] ? -1 : 1));
        if (this.orderByDirection === "desc") {
          sortedList.reverse();
        }
      } else {
        sortedList.sort((a, b) => {
          if (a[this.orderByProperty] == b[this.orderByProperty]) {
            return a["name"]["name"] < b["name"]["name"] ? -1 : 1;
          }

          if (this.orderByDirection === "desc") {
            return a[this.orderByProperty] > b[this.orderByProperty] ? -1 : 1;
          } else {
            return a[this.orderByProperty] < b[this.orderByProperty] ? -1 : 1;
          }
        });
      }
      sortedList.forEach(p => {
        p.selected = this.selectedPeople.some(sp => sp.userId === p.userId);
      });

      return sortedList;
    },

    onClick(person) {
      if (this.isPersonDisabled(person)) {
        return;
      }

      this.$emit("personClick", person);
    },

    isPersonEmphasized(person) {
      return this.emphasizePersonKey && person[this.emphasizePersonKey];
    },

    isPersonDisabled(person) {
      return this.disabledPeopleIds && this.disabledPeopleIds.indexOf(person?.userId) > -1;
    },

    calculateSortDirectionForColumn(columnName) {
      if (this.orderByProperty === columnName) {
        return this.orderByDirection === "asc" ? "ascending" : "descending";
      } else {
        return undefined;
      }
    },

    calcRouteParams(person) {
      const paramsList = Object.keys(this.onClickRouteParams);

      const newParamsList = {};

      paramsList.forEach(p => {
        if (typeof this.onClickRouteParams[p] == "string" && this.onClickRouteParams[p].startsWith(":")) {
          const propertyNameOnPersonObject = this.onClickRouteParams[p].substring(1);
          newParamsList[p] = person[propertyNameOnPersonObject];
        } else {
          newParamsList[p] = this.onClickRouteParams[p];
        }
      });

      return { ...newParamsList };
    }
  },

  components: {
    PersonAvatar
  }
};
</script>

<style scoped>
.min-width {
  min-width: 1rem;
}

.max-width-25pct {
  max-width: 25%;
}
</style>
